import { createTheme, responsiveFontSizes } from '@mui/material/styles';

/**
 * Ardinotech "Interface" theme.
 *
 * Visual direction: refined SaaS — deep navy (drawn from the AT logo) with
 * a violet accent for primary CTAs, glass surfaces over a soft mesh.
 * Display typography uses Outfit (geometric, slightly futuristic) paired
 * with Manrope for body text. Both are loaded from Google Fonts in
 * index.html (or via @fontsource if you prefer).
 */

// Brand tokens — exposed for direct use outside MUI's theme.
export const brand = {
  navyDeepest: '#0B1530',
  navy: '#152346',
  navyLight: '#2A3A6A',
  violet: '#7B61FF',
  violetSoft: '#A78BFA',
  violetGlow: 'rgba(123, 97, 255, 0.35)',
  glassBg: 'rgba(255, 255, 255, 0.55)',
  glassBgDark: 'rgba(11, 21, 48, 0.55)',
  glassBorder: 'rgba(255, 255, 255, 0.35)',
  meshFrom: '#EEF1FB',
  meshTo: '#F7F2FF',
  ink: '#0E1326',
  inkSoft: '#4A4F6E',
};

let theme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: brand.violet,
      light: brand.violetSoft,
      dark: '#5B47C9',
      contrastText: '#ffffff',
    },
    secondary: {
      main: brand.navy,
      light: brand.navyLight,
      dark: brand.navyDeepest,
      contrastText: '#ffffff',
    },
    background: {
      default: brand.meshFrom,
      paper: '#ffffff',
    },
    text: {
      primary: brand.ink,
      secondary: brand.inkSoft,
    },
    divider: 'rgba(11, 21, 48, 0.08)',
  },

  typography: {
    fontFamily:
      '"Manrope", "Plus Jakarta Sans", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
    fontSize: 14,
    htmlFontSize: 16,
    h1: {
      fontFamily: '"Outfit", "Manrope", sans-serif',
      fontSize: '2.4rem',
      fontWeight: 700,
      letterSpacing: '-0.02em',
    },
    h2: {
      fontFamily: '"Outfit", "Manrope", sans-serif',
      fontSize: '1.35rem',
      fontWeight: 700,
      letterSpacing: '-0.015em',
    },
    h3: {
      fontFamily: '"Outfit", "Manrope", sans-serif',
      fontSize: '1.35rem',
      fontWeight: 600,
      letterSpacing: '-0.01em',
    },
    h4: {
      fontFamily: '"Outfit", "Manrope", sans-serif',
      fontSize: '1.1rem',
      fontWeight: 600,
    },
    button: { textTransform: 'none', fontWeight: 600, letterSpacing: '0.01em' },
  },

  shape: { borderRadius: 12 },

  components: {
    MuiButton: {
      defaultProps: { disableElevation: true },
      styleOverrides: {
        root: {
          minHeight: 42,
          borderRadius: 10,
          paddingInline: 18,
        },
        containedPrimary: {
          background: `linear-gradient(135deg, ${brand.violet} 0%, ${brand.violetSoft} 100%)`,
          boxShadow: `0 8px 24px -8px ${brand.violetGlow}`,
          '&:hover': {
            background: `linear-gradient(135deg, #6B52E8 0%, ${brand.violet} 100%)`,
            boxShadow: `0 12px 28px -6px ${brand.violetGlow}`,
          },
        },
        outlinedPrimary: {
          borderColor: 'rgba(123, 97, 255, 0.4)',
          '&:hover': {
            borderColor: brand.violet,
            background: 'rgba(123, 97, 255, 0.06)',
          },
        },
      },
    },
    MuiTextField: {
      defaultProps: { size: 'small', variant: 'outlined' },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: 10,
          background: '#ffffff',
          '& input': { fontSize: 15 },
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: brand.violetSoft,
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderWidth: 1.5,
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 16,
          boxShadow: '0 1px 3px rgba(11, 21, 48, 0.06), 0 8px 32px -12px rgba(11, 21, 48, 0.12)',
          border: '1px solid rgba(11, 21, 48, 0.06)',
        },
      },
    },
    MuiAppBar: {
      defaultProps: { elevation: 0 },
      styleOverrides: {
        root: { borderBottom: '1px solid rgba(11, 21, 48, 0.06)' },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: { minHeight: 40, minWidth: 40 },
      },
    },
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          backgroundImage: `radial-gradient(at 20% 0%, ${brand.meshTo} 0%, transparent 50%), radial-gradient(at 80% 100%, #E8EDFB 0%, transparent 50%)`,
          backgroundAttachment: 'fixed',
          minWidth: 480,
        },
        // Hide native up/down spinner arrows on number inputs everywhere — render as a plain text field.
        'input[type=number]': {
          MozAppearance: 'textfield',
        },
        'input[type=number]::-webkit-outer-spin-button, input[type=number]::-webkit-inner-spin-button': {
          WebkitAppearance: 'none',
          margin: 0,
        },
      },
    },
  },
});

theme = responsiveFontSizes(theme);

export default theme;
